/**
 * Admin → Position Types tab — feature E05-F04 (E05-F04-T04).
 *
 * 1:1 port of the legacy `dashboard.js` position-type UI (renderPositionTypes /
 * openPositionTypeEditor / savePositionType / deletePositionType) +
 * `index.html#admin-content-postypes` + the position-type dialog. This is the
 * admin Settings tab that LISTS every active `position_types` row as a card and
 * lets an admin create / edit / soft-delete a type with its extra questions.
 *
 * Parity, card-by-card (legacy renderPositionTypes):
 *   - card shows `name` + the extra questions; when a type has none it shows
 *     "No additional questions" (acceptance T04);
 *   - Edit / Delete on the card.
 *
 * Dialog (legacy openPositionTypeEditor + savePositionType):
 *   - `name` REQUIRED — a blank name blocks the save with the error toast
 *     "Name is required" (PT_NAME_REQUIRED_MESSAGE from @eer/shared);
 *   - the extra-questions list is DYNAMIC: add / remove rows, each row a single
 *     text input; every kept question is stored as `{ text, category:'Position-
 *     Specific' }`;
 *   - EMPTY (blank-text) rows are DROPPED before save (shared
 *     `sanitizePositionTypeQuestions`) — acceptance T04.
 *
 * Transport: questionnaireApi.ts hooks → the shared `apiClient`. A successful
 * create/update/delete invalidates the list so the change re-renders.
 */
import { useState } from 'react';
import {
  PT_NAME_REQUIRED_MESSAGE,
  sanitizePositionTypeQuestions,
  type PositionTypeDTO,
  type PositionTypeSaveBody,
} from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useCreatePositionType,
  useDeletePositionType,
  usePositionTypes,
  useUpdatePositionType,
} from './questionnaireApi.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

/** Seed the dynamic question-text rows from a type (edit) or one blank (create). */
function questionTextsFromType(type: PositionTypeDTO | null): string[] {
  if (!type || type.questions.length === 0) return [''];
  return type.questions.map((q) => q.text);
}

/** Build the save body. `name` trimmed; empty-text rows dropped + category forced. */
function bodyFromForm(name: string, texts: ReadonlyArray<string>): PositionTypeSaveBody {
  return {
    name: name.trim(),
    questions: sanitizePositionTypeQuestions(texts.map((text) => ({ text }))),
  };
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

interface TypeDialogProps {
  readonly type: PositionTypeDTO | null;
  readonly onClose: () => void;
}

function TypeDialog({ type, onClose }: TypeDialogProps): React.JSX.Element {
  const toast = useToast();
  const createMut = useCreatePositionType();
  const updateMut = useUpdatePositionType();
  const [name, setName] = useState<string>(type?.name ?? '');
  const [texts, setTexts] = useState<string[]>(() => questionTextsFromType(type));
  const isEdit = type !== null;
  const pending = createMut.isPending || updateMut.isPending;

  function setText(index: number, value: string): void {
    setTexts((prev) => prev.map((t, i) => (i === index ? value : t)));
  }
  function addRow(): void {
    setTexts((prev) => [...prev, '']);
  }
  function removeRow(index: number): void {
    setTexts((prev) => (prev.length <= 1 ? [''] : prev.filter((_, i) => i !== index)));
  }

  function handleSave(): void {
    const body = bodyFromForm(name, texts);
    if (body.name.length === 0) {
      toast.error(PT_NAME_REQUIRED_MESSAGE); // "Name is required"
      return;
    }
    const onSuccess = (): void => {
      toast.success('Type saved');
      onClose();
    };
    const onError = (error: unknown): void => {
      toast.error(errorMessage(error, 'Save error'));
    };
    if (isEdit && type) {
      updateMut.mutate({ id: type.id, body }, { onSuccess, onError });
    } else {
      createMut.mutate(body, { onSuccess, onError });
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit position type' : 'Add position type'}
      maxWidth="lg"
      testId="postype-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="postype-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="postype-field-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Name</span>
          <input
            id="postype-field-name"
            data-testid="postype-field-name"
            type="text"
            className={FIELD_CLASS}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-text-strong">Additional questions</span>
            <button
              type="button"
              data-testid="postype-add-question"
              onClick={addRow}
              className="text-xs text-primary hover:underline"
            >
              <i className="fa-solid fa-plus mr-1" /> Add question
            </button>
          </div>
          <div data-testid="postype-questions" className="grid gap-2">
            {texts.map((text, index) => (
              <div
                key={index}
                data-testid="postype-question-row"
                className="flex items-center gap-2"
              >
                <input
                  data-testid="postype-question-input"
                  type="text"
                  className={FIELD_CLASS}
                  value={text}
                  placeholder="Question text"
                  onChange={(e) => setText(index, e.target.value)}
                />
                <button
                  type="button"
                  data-testid="postype-remove-question"
                  onClick={() => removeRow(index)}
                  className="shrink-0 text-gray-400 hover:text-danger"
                  aria-label="Remove question"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Type card
// ---------------------------------------------------------------------------

interface TypeCardProps {
  readonly type: PositionTypeDTO;
  readonly onEdit: (t: PositionTypeDTO) => void;
  readonly onDelete: (t: PositionTypeDTO) => void;
}

function TypeCard({ type, onEdit, onDelete }: TypeCardProps): React.JSX.Element {
  const hasQuestions = type.questions.length > 0;
  return (
    <div
      data-testid="postype-card"
      data-type-id={type.id}
      className="group rounded-lg border border-border p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <span data-testid="postype-name" className="text-sm font-medium text-text-strong">
          {type.name}
        </span>
        <div className="flex shrink-0 items-center gap-2 opacity-0 transition group-hover:opacity-100">
          <button
            type="button"
            data-testid="postype-edit"
            onClick={() => onEdit(type)}
            className="text-xs text-gray-400 hover:text-primary"
            aria-label="Edit type"
          >
            <i className="fa-solid fa-pen" />
          </button>
          <button
            type="button"
            data-testid="postype-delete"
            onClick={() => onDelete(type)}
            className="text-xs text-gray-400 hover:text-danger"
            aria-label="Delete type"
          >
            <i className="fa-solid fa-trash" />
          </button>
        </div>
      </div>
      {hasQuestions ? (
        <ul
          data-testid="postype-questions-list"
          className="mt-2 list-disc pl-5 text-xs text-gray-600"
        >
          {type.questions.map((q, i) => (
            <li key={i} data-testid="postype-question-item">
              {q.text}
            </li>
          ))}
        </ul>
      ) : (
        <p data-testid="postype-no-questions" className="mt-2 text-xs italic text-gray-400">
          No additional questions
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

/**
 * The Position Types admin tab. Lists active types as cards (server orders
 * sort_order, name) and opens the editor dialog for create / edit.
 */
export function PositionTypesTab(): React.JSX.Element {
  const query = usePositionTypes();
  const toast = useToast();
  const deleteMut = useDeletePositionType();
  // null → no dialog; 'new' → create; object → edit.
  const [editing, setEditing] = useState<PositionTypeDTO | 'new' | null>(null);

  const types = query.data ?? [];

  function handleDelete(t: PositionTypeDTO): void {
    deleteMut.mutate(t.id, {
      onSuccess: () => toast.success('Type deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete error')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="postype-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div data-testid="postype-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load position types')}
      </div>
    );
  }

  return (
    <div data-testid="postype-tab">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">Position Types</h2>
        <button
          type="button"
          data-testid="postype-add"
          onClick={() => setEditing('new')}
          className="rounded bg-primary px-3 py-2 text-sm text-surface hover:bg-primary-hover"
        >
          <i className="fa-solid fa-plus mr-1" aria-hidden /> Add Type
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500">
        Define position types with additional kick-off questions. Selected on Pre-Kickoff stage.
      </p>

      {types.length === 0 ? (
        <div data-testid="postype-empty" className="py-4 text-center text-gray-400">
          No position types
        </div>
      ) : (
        <div data-testid="postype-list" className="grid gap-3">
          {types.map((t) => (
            <TypeCard
              key={t.id}
              type={t}
              onEdit={(type) => setEditing(type)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {editing !== null ? (
        <TypeDialog type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

export default PositionTypesTab;
